import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../global/functions/apiClient";
import { getAuthHeaders } from "../global/functions/authorization";
const apiUrl = process.env.REACT_APP_API_URL;
const initialState = {
  items: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 1,
  },
  lastParams: null,
  query: { search: "", limit: 12, page: 1 },
  status: "idle",
  error: null,
  success: null,
  loading: false,
};

export const getProducts = createAsyncThunk(
  "product/getProducts",
  async (restaurantId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/product`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const getProductsPaginated = createAsyncThunk(
  "product/getProductsPaginated",
  async ({ restaurantId, search, limit, page }) => {
    try {
      const response = await axios.get(`${apiUrl}/product`, {
        ...getAuthHeaders(false, restaurantId),
        params: {
          search,
          limit,
          page,
        },
      });
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const getSingleProducts = createAsyncThunk(
  "product/getSingleProducts",
  async (restaurantId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/product/seul`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const addProduct = createAsyncThunk(
  "product/addProduct",
  async ({ body, restaurantId }) => {
    try {
      const response = await axios.post(
        `${apiUrl}/product`,
        body,
        getAuthHeaders(true, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const modifyProduct = createAsyncThunk(
  "product/modifyProduct",
  async ({ body, productId, restaurantId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/product/update/${productId}`,
        body,
        getAuthHeaders(true, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "product/deleteProduct",
  async ({ productId, restaurantId }) => {
    try {
      const response = await axios.delete(
        `${apiUrl}/product/${productId}`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const getProductByCategoryId = createAsyncThunk(
  "product/getProductByCategoryId",
  async ({ categoryId, restaurantId }) => {
    try {
      const response = await axios.get(
        `${apiUrl}/product/${categoryId}`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const setProductDiscount = createAsyncThunk(
  "product/setProductDiscount",
  async ({ productId, body, restaurantId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/product/discount/${productId}`,
        body,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const removeProductDiscount = createAsyncThunk(
  "product/removeProductDiscount",
  async ({ productId, restaurantId }) => {
    try {
      const response = await axios.delete(
        `${apiUrl}/product/discount/${productId}`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    updateStatus: (state) => {
      state.status = "idle";
    },
    setProductsQuery: (state, action) => {
      state.query = { ...state.query, ...action.payload };
    },
    resetProductsQuery: (state) => {
      state.query = { search: "", limit: 12, page: 1 };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getProducts.pending, (state, action) => {
        state.status = "loadingFetchData";
        state.loading = true;
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.status = "fetchData";
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(getProducts.rejected, (state, action) => {
        state.status = "fetchError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(getProductsPaginated.pending, (state, action) => {
        state.status = "loadingFetchDataPaginated";
        state.loading = true;
      })
      .addCase(getProductsPaginated.fulfilled, (state, action) => {
        state.status = "fetchDataPaginated";
        state.loading = false;
        state.items = action.payload.products || [];
        state.pagination = action.payload.pagination;
        state.lastParams = JSON.stringify(action.meta.arg);
      })
      .addCase(getProductsPaginated.rejected, (state, action) => {
        state.status = "fetchErrorPaginated";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(getSingleProducts.pending, (state, action) => {
        state.status = "loadingSingleProducts";
        state.loading = true;
      })
      .addCase(getSingleProducts.fulfilled, (state, action) => {
        state.status = "fetchSingleProducts";
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(getSingleProducts.rejected, (state, action) => {
        state.status = "singleProductsError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addProduct.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(addProduct.fulfilled, (state, action) => {
        state.status = "addSuccess";
        state.loading = false;
        state.success = action.payload.message;
        // The backend returns the created product's fields flattened on the
        // payload (alongside `message`), not nested under a `product` key.
        if (action.payload?._id) {
          const exists = state.items.some(
            (item) => item._id === action.payload._id
          );
          if (!exists) {
            state.items.unshift(action.payload);
            if (state.pagination) {
              state.pagination.totalRecords =
                (state.pagination.totalRecords || 0) + 1;
            }
          }
        }
      })
      .addCase(addProduct.rejected, (state, action) => {
        state.status = "addError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteProduct.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.status = "deleteSuccess";
        state.loading = false;
        state.success = action.payload.message;
        const deletedId = action.meta?.arg?.productId;
        if (deletedId) {
          state.items = state.items.filter((item) => item._id !== deletedId);
          if (state.pagination) {
            state.pagination.totalRecords = Math.max(
              0,
              (state.pagination.totalRecords || 0) - 1
            );
          }
        }
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.status = "deleteError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(modifyProduct.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(modifyProduct.fulfilled, (state, action) => {
        state.status = "modifySuccess";
        state.loading = false;
        state.success = action.payload.message;
        // The backend returns the updated product's fields flattened on the
        // payload (alongside `message`), not nested under a `product` key.
        if (action.payload?._id) {
          const productIndex = state.items.findIndex(
            (item) => item._id === action.payload._id
          );
          if (productIndex !== -1) {
            state.items[productIndex] = action.payload;
          }
        }
      })
      .addCase(modifyProduct.rejected, (state, action) => {
        state.status = "modifyError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(getProductByCategoryId.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(getProductByCategoryId.fulfilled, (state, action) => {
        state.status = "fetchDataByCategory";
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(getProductByCategoryId.rejected, (state, action) => {
        state.status = "fetchErrorByCategory";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(setProductDiscount.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(setProductDiscount.fulfilled, (state, action) => {
        state.status = "discountSetSuccess";
        state.loading = false;
        state.success = action.payload.message;
        // The backend spreads the updated product's fields directly onto the
        // payload (alongside `message`), it does not nest them under `product`.
        const updatedProduct = action.payload?.product || action.payload;
        if (!updatedProduct || !updatedProduct._id) return;
        // Update the product in the items array
        const productIndex = state.items.findIndex(
          (item) => item._id === updatedProduct._id
        );
        if (productIndex !== -1) {
          state.items[productIndex] = updatedProduct;
        }
      })
      .addCase(setProductDiscount.rejected, (state, action) => {
        state.status = "discountSetError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(removeProductDiscount.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(removeProductDiscount.fulfilled, (state, action) => {
        state.status = "discountRemoveSuccess";
        state.loading = false;
        state.success = action.payload?.message;
        // The backend spreads the updated product's fields directly onto the
        // payload (alongside `message`), it does not nest them under `product`.
        const updatedProduct = action.payload?.product || action.payload;
        // Update the product in the items array
        if (updatedProduct && updatedProduct._id) {
          const productIndex = state.items.findIndex(
            (item) => item._id === updatedProduct._id
          );
          if (productIndex !== -1) {
            state.items[productIndex] = updatedProduct;
          }
        }
      })
      .addCase(removeProductDiscount.rejected, (state, action) => {
        state.status = "discountRemoveError";
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { updateStatus, setProductsQuery, resetProductsQuery } =
  productSlice.actions;
export const selectAllProducts = (state) => state.product.items;
export const selectProductsPagination = (state) => state.product.pagination;
export const selectProductsLastParams = (state) => state.product.lastParams;
export const selectProductsQuery = (state) => state.product.query;
export const getProductsStatus = (state) => state.product.status;
export const getProductsError = (state) => state.product.error;
export const getProductsSuccess = (state) => state.product.success;
export const getProductsisLoading = (state) => state.product.loading;
export default productSlice.reducer;
